import { action, computed, observable, runInAction, ObservableMap } from 'mobx';
import { AudioComment, Comment, INewComment, IServerComment, TextComment } from '../Models/Comment';
import { CommentService } from '../Services/CommentService';
import { IPublicUser } from '../_interfaces/IUser';
import { UserStore } from '../_stores/UserStore';
import { CommentEditState } from './CommentEditState';
import { UserFamilyStore } from './UserFamilyStore';

export class StoryPhotoCommentsStore {
    private commentService: CommentService;
    private userFamilyStore: UserFamilyStore;
    private userStore: UserStore;

    @observable
    private _topLevelCommentEditState?: CommentEditState;
    private _replyEditState?: CommentEditState;

    @observable
    private _replyTargetId?: string;

    @computed
    public get replyTargetId(): string | undefined {
        return this._replyTargetId;
    }

    @action
    public setReplyTargetId(id: string | undefined): void {
        this._replyTargetId = id;
        if (id) {
            this._replyEditState = new CommentEditState();
            this._topLevelCommentEditState = undefined;
        } else {
            this._replyEditState = undefined;
            this._topLevelCommentEditState = new CommentEditState();
        }
    }

    public commentsByPhotoAndStoryID = new ObservableMap<string, Array<Comment>>();

    public static formatStoryAndPhotoIdKey(storyId: number, photoId: number): string {
        return `${storyId}_${photoId}`;
    }

    public get replyEditState(): CommentEditState | undefined {
        return this._replyEditState;
    }

    public replaceReplyEditState(): CommentEditState {
        this._replyEditState = new CommentEditState();
        return this._replyEditState;
    }

    public constructor(token: string, userFamilyStore: UserFamilyStore, userStore: UserStore) {
        this.commentService = new CommentService(token);
        this.userFamilyStore = userFamilyStore;
        this.userStore = userStore;
    }

    @action
    public deleteParticularCommentStores(): void {
        this._topLevelCommentEditState = undefined;
        this._replyEditState = undefined;
    }

    @action
    public loadCommentsForStoryPhotoChange = async (storyID: number, photoID: number): Promise<void> => {
        const mergedComments = await this.loadCommentsForStoryAndPhoto(storyID, photoID);

        // ToDo: Now that we're on mobx 4, we should be able to migrate to mobx-flows/generators https://mobx.js.org/best/actions.html#flows .
        runInAction(() => {
            const key = StoryPhotoCommentsStore.formatStoryAndPhotoIdKey(storyID, photoID);
            this._replyTargetId = undefined;
            this.commentsByPhotoAndStoryID.set(key, mergedComments);
            this._topLevelCommentEditState = new CommentEditState();
            this._replyEditState = undefined;
        });
    }

    private static buildComment(comment: IServerComment, children: Array<Comment>, author: IPublicUser): Comment {
        return comment.text ? new TextComment(comment, children || [], author) : new AudioComment(comment, children || [], author);
    }

    public get hasUnsubmittedData(): boolean {
        return !!this._topLevelCommentEditState && this._topLevelCommentEditState!.hasUnsubmittedData ||
            (!!this.replyEditState && this.replyEditState.hasUnsubmittedData);
    }

    @computed
    public get topLevelCommentEditState(): CommentEditState {
        return this._topLevelCommentEditState!;
    }

    @action
    public enableTopLevelCommentEditor(): void {
        this._topLevelCommentEditState = new CommentEditState();
        this._replyTargetId = undefined;
        this._replyEditState = undefined;
    }

    private async loadCommentsForStoryAndPhoto(storyID: number, photoID: number): Promise<Array<Comment>> {
        const comments = await this.commentService.getCommentsForStoryAndPhoto(storyID, photoID);

        const rootComments = comments.filter(comment => comment.parent_id === null);
        const childCommentsByParentID = comments
            .filter(comment => comment.parent_id)
            .sort((a, b) => a.created_at - b.created_at)
            .reduce((childrenById, comment) => {
                const author = this.userFamilyStore.users.find(user => user.id === comment.user_id);
                if (!author) {
                    return childrenById;
                }
                const commentsForParentID = (childrenById as any)[comment.parent_id!] || [];
                return { ...childrenById, [comment.parent_id!]: [...commentsForParentID, StoryPhotoCommentsStore.buildComment(comment, [], author)] };
            }, {});

        return rootComments.sort((a, b) => a.created_at - b.created_at).map(comment => {
            const author =
                comment.user_id === this.userStore.currentUser.id
                    ? this.userStore.currentUser
                    : this.userFamilyStore.users.find(user => user.id === comment.user_id);

            return StoryPhotoCommentsStore.buildComment(comment, (childCommentsByParentID as any)[comment._id], author!);
        });
    }

    @action
    public postComment = async (storyId: number, photoId: number, parentId?: string): Promise<void> => {
        let commentEditState: CommentEditState;
        if (parentId) {
            commentEditState = this._replyEditState!;
        } else {
            commentEditState = this._topLevelCommentEditState!;
        }

        await this.commentService.postCommentForPhoto({
            collection_id: storyId,
            photo_id: photoId,
            parent_id: parentId,
            audio_file: commentEditState.audio,
            audio_length_ms: commentEditState.audioLengthMs,
            text: commentEditState.commentText
        } as INewComment);

        const reloadPromise = this.loadCommentsForStoryAndPhoto(storyId, photoId);

        // ToDo: Now that we're on mobx 4, we should be able to migrate to mobx-flows/generators https://mobx.js.org/best/actions.html#flows .
        runInAction(() => {
            if (parentId) {
                this._replyEditState = new CommentEditState();
                this._replyTargetId = undefined;
                this._topLevelCommentEditState = new CommentEditState();
            } else {
                this._topLevelCommentEditState = new CommentEditState();
            }
        });

        const mergedComments = await reloadPromise;

        // ToDo: Now that we're on mobx 4, we should be able to migrate to mobx-flows/generators https://mobx.js.org/best/actions.html#flows .
        runInAction(() => {
            const key = StoryPhotoCommentsStore.formatStoryAndPhotoIdKey(storyId, photoId);
            this.commentsByPhotoAndStoryID.set(key, mergedComments);
        });

    }
}



// WEBPACK FOOTER //
// ./src/Stores/StoryPhotoCommentsStore.ts